import type { HumanName, TokenOption } from '@heartplus/api';

import type { BlockNoteJsonValue, TokenOptionPresentation } from './types';

type TokenValueInput = BlockNoteJsonValue | undefined;

/**
 * Returns the best display text from a generated HumanName value.
 */
export function getHumanNameText(name: HumanName | undefined): string {
  return name?.defaultText || name?.i18nKey || '';
}

/**
 * Serializes token defaults into the string value expected by BlockNote props.
 */
export function stringifyTokenValue(value: TokenValueInput): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  if (typeof value === 'object') {
    const maybeMultiValue = value as { values?: BlockNoteJsonValue[] };
    if (Array.isArray(maybeMultiValue.values)) {
      return JSON.stringify(maybeMultiValue.values);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

/**
 * Renders a `duration` token's value — canonical integer seconds — as a compact
 * human string (e.g. `"529"` → `"8m 49s"`). The stored value stays numeric on the
 * storage/validation paths (a formatted string like `"8m 49s"` would be rejected
 * by duration validation), so the conversion to prose happens at the display seam
 * only — both here (client read mode / editable-text flatten) and in the Go
 * `formatDurationSeconds` (notes `note_template_editable_text_flatten.go`). The two
 * are kept BYTE-IDENTICAL.
 *
 * A non-integer payload is returned UNCHANGED so legacy unit-glued rows (`"529 s"`),
 * the unresolved placeholder (`"_"`), an empty string, and any free text are never
 * mangled. The single shared implementation so every consumer formats identically.
 */
export function formatDurationSeconds(raw: string): string {
  const trimmed = raw.trim();
  // `Number.parseInt` would accept `"529 s"` (parses the leading digits and drops
  // the rest) — that is exactly the legacy garble we must NOT touch. Require the
  // whole trimmed string to be an integer, otherwise return the input verbatim.
  if (!/^-?\d+$/.test(trimmed)) {
    return raw;
  }
  return formatDurationHMS(Number.parseInt(trimmed, 10));
}

/**
 * Turns a whole number of seconds into a compact day/hour/minute/second string,
 * emitting only the NON-ZERO units (`529` → `"8m 49s"`, `3600` → `"1h"`,
 * `86400` → `"1d"`, `90061` → `"1d 1h 1m 1s"`) and flooring an all-zero total to
 * `"0s"`. A negative value is clamped to `"0s"` defensively — a duration cannot be
 * negative, and a leading `"-"` before the largest unit would read as garbage prose.
 * Mirror of the Go `formatDurationHMS`.
 */
function formatDurationHMS(total: number): string {
  const seconds = total < 0 ? 0 : total;
  const parts: [number, string][] = [
    [Math.floor(seconds / 86400), 'd'],
    [Math.floor((seconds % 86400) / 3600), 'h'],
    [Math.floor((seconds % 3600) / 60), 'm'],
    [seconds % 60, 's'],
  ];
  const out = parts
    .filter(([value]) => value !== 0)
    .map(([value, unit]) => `${value}${unit}`);
  return out.length === 0 ? '0s' : out.join(' ');
}

/**
 * Converts API token options into the compact presentation shape stored in
 * BlockNote token props.
 */
export function convertTokenOptions(
  options: TokenOption[],
): TokenOptionPresentation[] {
  return options.map((option) => ({
    label: getHumanNameText(option.label) || option.value,
    value: option.value,
    className: option.className,
    presentingRhythmCategory: getHumanNameText(option.presentingRhythmCategory),
  }));
}

/**
 * Returns the first matching option value using case-insensitive comparison.
 */
export function findMatchingOptionValue(
  options: TokenOption[],
  value: string,
): string | undefined {
  if (!value || options.length === 0) {
    return undefined;
  }

  const lowerValue = value.toLowerCase().trim();
  return options.find((opt) => opt.value.toLowerCase().trim() === lowerValue)
    ?.value;
}

/**
 * Parses a token value that may represent a JSON string array.
 */
export function parseTokenValueList(value: TokenValueInput): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter((item) => item !== '');
  }

  if (typeof value === 'string') {
    if (!value.trim()) {
      return [];
    }

    try {
      const parsed = JSON.parse(value) as BlockNoteJsonValue;
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item)).filter((item) => item !== '');
      }
    } catch {
      return [value.trim()];
    }

    return [value.trim()];
  }

  if (value && typeof value === 'object') {
    const maybeValues = value as { values?: BlockNoteJsonValue[] };
    if (Array.isArray(maybeValues.values)) {
      return maybeValues.values
        .map((item) => String(item))
        .filter((item) => item !== '');
    }
  }

  return [];
}
